import { useTranslation } from 'react-i18next';
import { Users, Trophy } from 'lucide-react';
import type { TeamRankingItem } from '@/types/session.ts';
import { toScore99 } from '@/utils/scoreFormat.ts';

interface TeamRankingGridProps {
  teams: TeamRankingItem[];
  currentTeamId?: string;
}

function getTierConfig(tier: string) {
  switch (tier) {
    case 'elite':
      return { label: 'Elite', color: '#C9A84C', bg: 'from-yellow-900/20 to-yellow-800/5', border: 'border-yellow-700/30' };
    case 'pro':
      return { label: 'Pro', color: 'var(--primary)', bg: 'from-purple-900/20 to-purple-800/5', border: 'border-purple-700/30' };
    default:
      return { label: 'Standard', color: 'var(--color-gray-400)', bg: 'from-gray-800/20 to-gray-700/5', border: 'border-border' };
  }
}

export default function TeamRankingGrid({ teams, currentTeamId }: TeamRankingGridProps) {
  const { t } = useTranslation();

  if (teams.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-muted py-12">
        <p className="text-sm font-medium text-muted-foreground">
          {t('ranking.noTeams')}
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {teams.map((team, index) => {
        const tier = getTierConfig(team.tier);
        const isCurrentTeam = currentTeamId === team.teamId;
        const score99 = toScore99(team.averageScore);

        return (
          <div
            key={team.teamId}
            className={`relative overflow-hidden rounded-xl border bg-gradient-to-br p-5 ${tier.border} ${tier.bg}`}
          >
            {/* Position badge */}
            <div className="absolute -left-1 -top-1 z-10 flex h-8 w-8 items-center justify-center rounded-br-xl rounded-tl-xl bg-card border border-border text-xs font-bold text-foreground shadow-sm">
              #{index + 1}
            </div>

            {/* Current team badge */}
            {isCurrentTeam && (
              <div className="absolute -right-1 -top-1 z-10 rounded-bl-xl rounded-tr-xl bg-primary px-2.5 py-1 text-[10px] font-bold text-white shadow-sm">
                {t('ranking.myTeam')}
              </div>
            )}

            <div className="mt-4 text-center">
              {/* Trophy for #1 */}
              {index === 0 && (
                <Trophy className="mx-auto mb-1 h-6 w-6 text-yellow-500" />
              )}

              <h3 className="text-lg font-bold text-foreground">{team.teamName}</h3>

              {/* Tier badge */}
              <span
                className="mt-1 inline-block rounded-full px-3 py-0.5 text-xs font-bold"
                style={{ color: tier.color, backgroundColor: `${tier.color}15` }}
              >
                {tier.label}
              </span>

              {/* Score */}
              <div className="mt-3">
                <span className="text-3xl font-black" style={{ color: tier.color }}>
                  {score99}
                </span>
                <span className="text-sm text-muted-foreground">/99</span>
              </div>

              {/* Stats row */}
              <div className="mt-3 flex items-center justify-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  <span>{team.memberCount} {t('ranking.members')}</span>
                </div>
                <div>
                  {team.totalSessions} {t('ranking.sessions')}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
